import { useState, useEffect } from 'react';

export interface DeviceCapabilities {
  isHighPerformance: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  prefersReducedMotion: boolean;
  supportsWillChange: boolean;
  devicePixelRatio: number;
  memoryInfo?: any;
  connectionType?: string;
}

export const useDeviceCapabilities = (): DeviceCapabilities => {
  const [capabilities, setCapabilities] = useState<DeviceCapabilities>({
    isHighPerformance: true, // Default to true, detect false
    isMobile: false,
    isTablet: false,
    isDesktop: true,
    prefersReducedMotion: false,
    supportsWillChange: true,
    devicePixelRatio: 1,
  });

  useEffect(() => {
    const detectCapabilities = (): DeviceCapabilities => {
      // Screen size detection
      const width = window.innerWidth;
      const isMobile = width < 768;
      const isTablet = width >= 768 && width < 1024;
      const isDesktop = width >= 1024;

      // Performance indicators
      const devicePixelRatio = window.devicePixelRatio || 1;
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      
      // Feature detection
      const supportsWillChange = CSS.supports('will-change', 'transform');
      
      // Memory and connection detection (if available)
      const memoryInfo = (navigator as any)?.deviceMemory;
      const connectionInfo = (navigator as any)?.connection;
      const connectionType = connectionInfo?.effectiveType || connectionInfo?.type;

      // Performance estimation based on multiple factors
      let isHighPerformance = true;

      // Reduce performance rating for mobile devices
      if (isMobile) {
        isHighPerformance = false;
      }

      // High DPI screens might struggle with complex animations
      if (devicePixelRatio > 2) {
        isHighPerformance = false;
      }

      // Low memory devices
      if (memoryInfo && memoryInfo < 4) {
        isHighPerformance = false;
      }

      // Slow connections might indicate weaker devices
      if (connectionType && (connectionType === '2g' || connectionType === 'slow-2g')) {
        isHighPerformance = false;
      }

      // User preference for reduced motion
      if (prefersReducedMotion) {
        isHighPerformance = false;
      }

      // Simple performance benchmark
      const startTime = performance.now();
      const testElement = document.createElement('div');
      testElement.style.transform = 'translate3d(100px, 100px, 0) rotate(45deg) scale(1.5)';
      document.body.appendChild(testElement);
      
      // Force reflow
      testElement.getBoundingClientRect();
      
      const endTime = performance.now();
      document.body.removeChild(testElement);
      
      // If transform takes too long, likely a slower device
      if (endTime - startTime > 2) {
        isHighPerformance = false;
      }

      return {
        isHighPerformance,
        isMobile,
        isTablet,
        isDesktop,
        prefersReducedMotion,
        supportsWillChange,
        devicePixelRatio,
        memoryInfo,
        connectionType,
      };
    };

    setCapabilities(detectCapabilities());

    // Re-detect on resize
    const handleResize = () => {
      const newCapabilities = detectCapabilities();
      setCapabilities(prev => ({
        ...prev,
        ...newCapabilities,
        // Keep performance detection stable unless major changes
        isHighPerformance: prev.isHighPerformance && newCapabilities.isHighPerformance,
      }));
    };

    window.addEventListener('resize', handleResize, { passive: true });

    // Listen for reduced motion preference changes
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleMotionChange = (e: MediaQueryListEvent) => {
      setCapabilities(prev => ({
        ...prev,
        prefersReducedMotion: e.matches,
        isHighPerformance: prev.isHighPerformance && !e.matches,
      }));
    };

    motionQuery.addEventListener('change', handleMotionChange);

    return () => {
      window.removeEventListener('resize', handleResize);
      motionQuery.removeEventListener('change', handleMotionChange);
    };
  }, []);

  return capabilities;
};

// Utility function to get animation config based on device capabilities
export const getAnimationConfig = (capabilities: DeviceCapabilities) => {
  if (capabilities.prefersReducedMotion) {
    return {
      duration: 0.01,
      ease: 'linear' as const,
      enabled: false,
      complex: false,
    };
  }

  if (!capabilities.isHighPerformance) {
    return {
      duration: capabilities.isMobile ? 0.2 : 0.3,
      ease: 'easeOut' as const,
      enabled: true,
      complex: false,
    };
  }

  return {
    duration: capabilities.isDesktop ? 0.4 : 0.3,
    ease: [0.25, 0.46, 0.45, 0.94] as const,
    enabled: true,
    complex: capabilities.isDesktop,
  };
};